import clsx from 'clsx';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router';
import { Icon } from '../Icon/Icon';
import styles from './Button.module.css';

export type ButtonVariant = 'primary' | 'lime' | 'saffron' | 'ghost' | 'white';

interface Common {
  variant?: ButtonVariant;
  size?: 'md' | 'sm';
  block?: boolean;
  className?: string;
  children?: ReactNode;
}

function buttonClass({ variant = 'primary', size = 'md', block, className }: Common & { disabled?: boolean }) {
  return clsx(styles.btn, styles[variant], size === 'sm' && styles.sm, block && styles.block, className);
}

export interface ButtonProps extends Common, Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  loading?: boolean;
}

/** Pill button (`.btn`). `loading` keeps the label and adds a spinner + aria-busy. */
export function Button({
  variant,
  size,
  block,
  className,
  loading,
  disabled,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={clsx(buttonClass({ variant, size, block, className }), (disabled || loading) && styles.disabled)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading && <Icon name="spinner" size={18} strokeWidth={2.4} className={styles.spin} />}
      {children}
    </button>
  );
}

export interface ButtonLinkProps extends Common, Omit<LinkProps, 'className'> {}

/** Same look, as a router link. */
export function ButtonLink({ variant, size, block, className, children, ...rest }: ButtonLinkProps) {
  return (
    <Link className={buttonClass({ variant, size, block, className })} {...rest}>
      {children}
    </Link>
  );
}
